import {FormControl, UntypedFormGroup} from '@angular/forms';
import {MapOptions} from '../../../modules/map/classes/map-options';
import {LeafletMapLayer} from '../../../modules/layer/classes/leaflet-map-layer';
import {HeadoutSource} from '../../../modules/layer/sources/headout-source';
import {MapService} from '../../../modules/map/services/map.service';
import {LayerService} from '../../../modules/layer/services/layer.service';
import {catchError, filter, take, takeUntil} from 'rxjs/operators';
import {of, Subject} from 'rxjs';
import {HeadoutTableData} from '../interfaces/headout-table-data.interface';
import {LeafletLegendService} from '../../../modules/layer/services/leaflet-legend.service';
import {SystemEvent} from '../../../modules/system-event/classes/system-event';
import {HeadoutService} from '../headout.service';
import {Directive, inject, OnDestroy, signal} from '@angular/core';
import {SystemEventType} from '../../../modules/system-event/classes/system-event-type';
import {HeadoutEventResultItem} from './headout-event-result-item';
import {GroupedSystemEvents} from '../../../modules/system-event/classes/grouped-system-events';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HeadoutEventResult} from './headout-event-result';

@Directive()
export class HeadoutMapControls implements OnDestroy {
    public mapService = inject(MapService);
    public layerService = inject(LayerService);
    public legendService = inject(LeafletLegendService);
    private snackBar = inject(MatSnackBar);

    private headoutService = inject(HeadoutService);

    public filterGroup: UntypedFormGroup;

    public updateTableData: Subject<HeadoutTableData[]> = new Subject<HeadoutTableData[]>();

    public mapOptions: MapOptions;
    public mapLayer: LeafletMapLayer;
    public layerSource: HeadoutSource;

    public selectedDetail: HeadoutEventResult[];

    public eventGroups = signal({});

    initialEvent: any;

    private destroy$ = new Subject();

    constructor() {
        this.initializeMapOptions();
        this.initializeControls();
    }

    ngOnDestroy() {
        this.destroy$.next(undefined);
        this.destroy$.complete();
    }

    public initializeMapOptions(): void {
        this.mapOptions = new MapOptions().setZoom(3, 1);
        this.mapOptions.onlyManualZoom = true;
        this.mapOptions.show = {
            sidebar: false,
            refresh: false,
            export: false,
            coverage: false,
            coordinate: false,
            zoom: true,
            panControl: true,
        };
    }

    private initializeControls() {
        const controls = {
            type: new FormControl<SystemEventType>(null),
            name: new FormControl<SystemEvent>(null),
            advisory: new FormControl<HeadoutEventResult>(null),
        };

        // Change Handlers
        // Type Change
        controls.type.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((eventType: SystemEventType) => {
            this.resetForm(true, true);

            this.selectedDetail = undefined;

            this.headoutService
                .getHeadoutEventsByType(eventType)
                .pipe(take(1))
                .subscribe((events: GroupedSystemEvents) => {
                    this.eventGroups.set(events);

                    if (this.initialEvent) {
                        const selected = [...events.active, ...events.inactive].find((event) => this.initialEvent.id === event.id);
                        controls.name.setValue(selected);
                    }
                });
        });

        // Event Change
        controls.name.valueChanges
            .pipe(
                filter((value) => !!value),
                takeUntil(this.destroy$)
            )
            .subscribe((value: SystemEvent) => {
                this.resetForm();
                this.headoutService
                    .getListResults(value)
                    .pipe(
                        catchError((error) => {
                            console.error(error);
                            this.snackBar.open(`No results found for ${value.name}`, 'X', {duration: 5_000});
                            return of([]);
                        })
                    )
                    .subscribe((detail: HeadoutEventResult[]) => {
                        this.selectedDetail = detail;
                        this.mapLayer.startLoading();
                        this.layerSource.processFeatures(detail);

                        this.layerSource.addToMap(true);
                        this.mapLayer.endLoading();

                        if (detail && detail.length > 0) {
                            controls.advisory.setValue(detail[0]);
                        }
                    });
            });

        // Advisory Change
        controls.advisory.valueChanges
            .pipe(
                filter((value) => !!value),
                takeUntil(this.destroy$)
            )
            .subscribe((value) => {
                const event = controls.name.value;
                this.resetForm(false, false);
                this.layerSource.restyleAdvisory(value.anlId);
                this.headoutService.getEventResults(value).subscribe((affected) => {
                    const tableData = affected.map((detail) => HeadoutEventResultItem.ConvertToReportData(detail));
                    this.updateTableData.next(tableData);
                    this.updateMapLayer(affected, event);
                });
                this.mapLayer.endLoading();
            });

        this.filterGroup = new UntypedFormGroup(controls);
    }

    private updateMapLayer(features: HeadoutEventResultItem[], event: SystemEvent): void {
        this.selectedDetail.forEach((a) => this.layerSource.processDetails(a, features, event));
        this.layerSource.addDetailsToMap();
        this.layerSource.fitToFeatures();
    }

    public resetForm(clearEvent: boolean = false, clearAdvisory: boolean = true) {
        this.updateTableData.next([]);
        if (clearAdvisory) {
            this.filterGroup.controls.advisory.reset();
            this.layerSource.clearOutages();
            this.layerSource.removeFromMap();
        }
        if (clearEvent) {
            this.filterGroup.controls.name.reset();
        }

        if (this.layerSource) {
            this.layerSource.selectedAdvisoryId = undefined;
            this.mapService.zoomToHome.next(undefined);
        }
    }

    public setInitialEvent(event: SystemEvent) {
        this.initialEvent = event;
        this.filterGroup.controls.type.setValue(event.type);
    }

    // private getSubLayerLegend(
    //   layerInfo: LeafletMapLayer,
    //   displayInfo: HeadoutInfrastructureDisplay | SourceDisplayInfo,
    //   styleName?: string
    // ): void {
    //   const legend = new LeafletLayerLegend(layerInfo.id, `Headout ${layerInfo.displayName}`);
    //   if (this.horizontalLegends) {
    //     legend.orientation = 'horizontal';
    //   }
    //
    //   if (styleName && this.horizontalLegends) {
    //     legend.orientation = 'gradient';
    //   }
    //
    //   const params = {
    //     REQUEST: 'GetLegendGraphic',
    //     VERSION: '1.0.0',
    //     FORMAT: 'application/json',
    //     LAYER: layerInfo.servicelayer,
    //   };
    //
    //   if (styleName) {
    //     params['STYLE'] = `headout_${styleName.toLowerCase()}`;
    //   }
    //
    //   const paramStr = Object.keys(params)
    //     .map((key) => `${key}=${params[key]}`)
    //     .join('&');
    //   this.layerService.getJsonFromUrl(`${ApplicationConfig.proxyPrefix}${layerInfo.serviceurl}?${paramStr}`).subscribe((res) => {
    //     legend.elements = res.Legend[0]?.rules?.map((rule) => {
    //       const ret = new LegendElement();
    //       const symbol = rule?.symbolizers[0];
    //
    //       ret.label = rule.name;
    //
    //       if (layerInfo.servicetype === GenServiceType.WMS) {
    //         let graphic: any;
    //         // We are dealing with a point
    //         if (symbol?.Point) {
    //           graphic = symbol?.Point?.graphics[0];
    //
    //           ret.width = symbol?.Point.size;
    //           ret.height = symbol?.Point.size;
    //           ret.labelColor = '#707070';
    //           ret.textSize = '12';
    //
    //           if (graphic) {
    //             ret.fillColor = graphic.fill;
    //             ret.strokeColor = graphic.stroke;
    //             ret.strokeWidth = graphic['stroke-width'];
    //
    //             if (graphic.mark === 'square') {
    //               ret.shape = 'rect';
    //             }
    //           }
    //         } else if (symbol?.Polygon) {
    //           graphic = symbol?.Polygon;
    //           ret.fillColor = graphic.fill;
    //           ret.strokeColor = graphic.stroke;
    //
    //           ret.shape = 'rect';
    //           legend.gapSize = 0;
    //
    //           if (legend.orientation === 'gradient') {
    //             legend.hideBorders = true;
    //             ret.textSize = '8';
    //             ret.width = 75;
    //             ret.height = 30;
    //             ret.labelPosition = 'inset';
    //             ret.labelColor = getFontColor(ret.fillColor);
    //           } else {
    //             ret.textSize = '12';
    //             ret.width = 20;
    //             ret.height = 12;
    //             ret.labelColor = '#707070';
    //           }
    //         }
    //       } else {
    //         ret.fillColor = symbol?.Polygon?.fill;
    //         ret.shape = 'rect';
    //       }
    //       return ret;
    //     });
    //
    //     displayInfo.legend = legend;
    //
    //     addActiveLegend(layerInfo.displayName, [legend]);
    //   });
    // }
}
